import React, { useState, useMemo, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useQueryCoordinate } from '../../clients/graphql/graphql.hooks';
import { TradingMetricsChart } from '../../components/TradingMetricsChart';
import './TradingMetricsPage.css';

const GET_TRADING_METRICS = gql`
  query TradingMetricsGet($input: TradingMetricsInput!) {
    tradingMetricsGet(input: $input) {
      overlaidMetrics {
        broker
        ordersCreated {
          time
          value
          delta
          percentage
        }
        ordersFilled {
          time
          value
          delta
          percentage
        }
        ordersCanceled {
          time
          value
          delta
          percentage
        }
        ordersFailed {
          time
          value
          delta
          percentage
        }
        ordersTotal {
          time
          value
          delta
          percentage
        }
      }
      summary {
        totalCreated
        totalFilled
        totalCanceled
        totalFailed
        totalCount
        filledPercentage
        canceledPercentage
        failedPercentage
        successRate
      }
      brokerMetrics {
        broker
        ordersCreated {
          time
          value
          delta
          percentage
        }
        ordersFilled {
          time
          value
          delta
          percentage
        }
        ordersCanceled {
          time
          value
          delta
          percentage
        }
        ordersFailed {
          time
          value
          delta
          percentage
        }
        ordersTotal {
          time
          value
          delta
          percentage
        }
      }
      systemMetrics {
        broker
        ordersCreated {
          time
          value
          delta
          percentage
        }
        ordersFilled {
          time
          value
          delta
          percentage
        }
        ordersCanceled {
          time
          value
          delta
          percentage
        }
        ordersFailed {
          time
          value
          delta
          percentage
        }
        ordersTotal {
          time
          value
          delta
          percentage
        }
      }
    }
  }
`;

export const TradingMetricsPage: React.FC = () => {
  const [selectedBroker, setSelectedBroker] = useState<string>('SYSTEM');
  const [showDelta, setShowDelta] = useState<boolean>(false);
  const [timeRange, setTimeRange] = useState<string>('24h');
  const [interval, setInterval] = useState<string>('30m');
  const [queryFrequency, setQueryFrequency] = useState<number>(300000); // Default 5 minutes
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  // Get valid intervals for a time range (ensure reasonable number of data points)
  const getValidIntervals = (timeRange: string): string[] => {
    const allIntervals = ['30s', '1m', '2m', '5m', '10m', '15m', '30m', '1h', '2h', '4h', '6h', '12h', '1d', '1w'];
    
    switch (timeRange) {
      case '5m': return ['30s', '1m'];
      case '15m': return ['30s', '1m', '2m'];
      case '30m': return ['30s', '1m', '2m', '5m'];
      case '1h': return ['1m', '2m', '5m', '10m'];
      case '3h': return ['2m', '5m', '10m', '15m'];
      case '6h': return ['5m', '10m', '15m', '30m'];
      case '12h': return ['10m', '15m', '30m', '1h'];
      case '24h': return ['15m', '30m', '1h', '2h'];
      case '3d': return ['30m', '1h', '2h', '4h'];
      case '7d': return ['1h', '2h', '4h', '6h'];
      default: return allIntervals;
    }
  };

  // Auto-adjust interval based on time range for better visualization
  const getRecommendedInterval = (timeRange: string) => {
    switch (timeRange) {
      case '5m': return '30s';
      case '15m': return '30s';
      case '30m': return '1m';
      case '1h': return '2m';
      case '3h': return '5m';
      case '6h': return '10m';
      case '12h': return '15m';
      case '24h': return '30m';
      case '3d': return '2h';
      case '7d': return '4h';
      default: return '10m';
    }
  };

  const getTimeRange = useCallback(() => {
    const now = new Date();
    const start = new Date();
    
    switch (timeRange) {
      case '5m':
        start.setMinutes(now.getMinutes() - 5);
        break;
      case '15m':
        start.setMinutes(now.getMinutes() - 15);
        break;
      case '30m':
        start.setMinutes(now.getMinutes() - 30);
        break;
      case '1h':
        start.setHours(now.getHours() - 1);
        break;
      case '3h':
        start.setHours(now.getHours() - 3);
        break;
      case '6h':
        start.setHours(now.getHours() - 6);
        break;
      case '12h':
        start.setHours(now.getHours() - 12);
        break;
      case '24h':
        start.setDate(now.getDate() - 1);
        break;
      case '3d':
        start.setDate(now.getDate() - 3);
        break;
      case '7d':
        start.setDate(now.getDate() - 7);
        break;
      case 'custom':
        // Validate custom range doesn't exceed 1 week
        const maxRangeMs = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds
        const startTimeObj = new Date(startTime);
        const endTimeObj = new Date(endTime);
        const requestedRangeMs = endTimeObj.getTime() - startTimeObj.getTime();
        
        if (requestedRangeMs > maxRangeMs) {
          // Cap the range to 1 week from start time
          const cappedEndTime = new Date(startTimeObj.getTime() + maxRangeMs);
          return { startTime: startTimeObj.toISOString(), endTime: cappedEndTime.toISOString() };
        }
        
        return { startTime, endTime };
      default:
        start.setDate(now.getDate() - 1);
    }
    
    return { startTime: start.toISOString(), endTime: now.toISOString() };
  }, [timeRange, startTime, endTime]);

  const parseInterval = (intervalStr: string) => {
    const match = intervalStr.match(/^(\d+)([smhd])$/);
    if (!match) return { intervalUnit: 'MINUTE', intervalCount: 5 };
    
    const [, count, unit] = match;
    const intervalCount = parseInt(count, 10);
    
    switch (unit) {
      case 's': return { intervalUnit: 'MINUTE', intervalCount: Math.max(1, Math.round(intervalCount / 60)) };
      case 'm': return { intervalUnit: 'MINUTE', intervalCount };
      case 'h': return { intervalUnit: 'HOUR', intervalCount };
      case 'd': return { intervalUnit: 'DAY', intervalCount };
      default: return { intervalUnit: 'MINUTE', intervalCount: 5 };
    }
  };

  const queryVariables = useMemo(() => {
    const timeRangeData = getTimeRange();
    const { intervalUnit, intervalCount } = parseInterval(interval);
    return {
      input: {
        ...timeRangeData,
        brokerName: selectedBroker === 'SYSTEM' ? undefined : selectedBroker,
        showDelta,
        intervalUnit,
        intervalCount
      }
    };
  }, [selectedBroker, showDelta, interval, getTimeRange]);

  const { data, loading, error, refetch } = useQueryCoordinate(GET_TRADING_METRICS, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    pollInterval: queryFrequency,
  });

  const handleTimeRangeChange = (startIndex: number, endIndex: number) => {
    // Handle timeline navigation from brush
    console.log('Time range changed:', { startIndex, endIndex });
  };

  if (loading && !data) {
    return (
      <div className="trading-metrics-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading trading metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trading-metrics-page">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Unable to load metrics</h2>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metricsData = data?.tradingMetricsGet;
  const availableBrokers = metricsData?.brokerMetrics || [];

  return (
    <div className="trading-metrics-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="title-section">
            <h1>Trading Metrics</h1>
            <div className="subtitle">Interactive overlaid analysis with advanced insights</div>
          </div>
          <div className="header-controls">
            <select 
              value={selectedBroker} 
              onChange={(e) => setSelectedBroker(e.target.value)}
              className="broker-select"
            >
              <option value="SYSTEM">System Total</option>
              {availableBrokers.map((broker: any) => (
                <option key={broker.broker} value={broker.broker}>
                  {broker.broker}
                </option>
              ))}
            </select>

            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className="time-range-select"
            >
              <option value="5m">Last 5 Minutes</option>
              <option value="15m">Last 15 Minutes</option>
              <option value="30m">Last 30 Minutes</option>
              <option value="1h">Last Hour</option>
              <option value="3h">Last 3 Hours</option>
              <option value="6h">Last 6 Hours</option>
              <option value="12h">Last 12 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="3d">Last 3 Days</option>
              <option value="7d">Last 7 Days</option>
              <option value="custom">Custom Range</option>
            </select>

            <div className="interval-control">
              <select 
                value={interval} 
                onChange={(e) => setInterval(e.target.value)}
                className="interval-select"
                title={`Recommended: ${getRecommendedInterval(timeRange)} for ${timeRange}`}
              >
                {getValidIntervals(timeRange).map(intervalOption => (
                  <option key={intervalOption} value={intervalOption}>
                    {intervalOption}
                  </option>
                ))}
              </select>
              {interval !== getRecommendedInterval(timeRange) && (
                <button
                  onClick={() => setInterval(getRecommendedInterval(timeRange))}
                  className="auto-interval-btn"
                  title={`Use recommended interval (${getRecommendedInterval(timeRange)})`}
                >
                  Auto
                </button>
              )}
            </div>

            <div className="query-frequency-control">
              <label>Refresh:</label>
              <select 
                value={queryFrequency} 
                onChange={(e) => setQueryFrequency(Number(e.target.value))}
                className="frequency-select"
              >
                <option value={30000}>30 seconds</option>
                <option value={60000}>1 minute</option>
                <option value={300000}>5 minutes</option>
                <option value={600000}>10 minutes</option>
                <option value={1800000}>30 minutes</option>
                <option value={0}>Manual only</option>
              </select>
            </div>

            <div className="view-mode-toggle">
              <label>Display Mode:</label>
              <div className="toggle-switch">
                <button
                  onClick={() => setShowDelta(false)}
                  className={!showDelta ? 'active' : ''}
                >
                  Absolute
                </button>
                <button
                  onClick={() => setShowDelta(true)}
                  className={showDelta ? 'active' : ''}
                >
                  Delta (Δ)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Time Range Inputs */}
      {timeRange === 'custom' && (
        <div className="custom-time-range">
          <div className="time-inputs">
            <div className="time-input-group">
              <label>Start Time:</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="time-input"
              />
            </div>
            <div className="time-input-group">
              <label>End Time:</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="time-input"
              />
            </div>
          </div>
          <div className="range-warning">
            ⚠️ Custom ranges are limited to 1 week maximum for performance
          </div>
        </div>
      )}

      {/* Main Chart */}
      <div className="main-content">
        {metricsData?.overlaidMetrics && metricsData?.summary ? (
          <TradingMetricsChart
            data={metricsData.overlaidMetrics}
            summary={metricsData.summary}
            showDelta={showDelta}
            isLoading={loading}
            onTimeRangeChange={handleTimeRangeChange}
          />
        ) : (
          <div className="no-data-state">
            <div className="no-data-icon">📊</div>
            <h3>No data available</h3>
            <p>No trading metrics found for the selected time range and broker.</p>
          </div>
        )}
      </div>

      {/* Additional Insights */}
      <div className="insights-section">
        <div className="insights-header">
          <h2>Performance Insights</h2>
          <div className="insights-subtitle">
            Key performance indicators and trends
          </div>
        </div>
        
        <div className="insights-grid">
          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">🎯</div>
              <h3>Fill Rate Efficiency</h3>
            </div>
            <div className="insight-content">
              <div className="insight-metric">
                {metricsData?.summary?.successRate?.toFixed(1) || 0}%
              </div>
              <p>Orders successfully filled vs created (excluding failed)</p>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">⚡</div>
              <h3>System Reliability</h3>
            </div>
            <div className="insight-content">
              <div className="insight-metric">
                {metricsData?.summary?.failedPercentage ? 
                  (100 - metricsData.summary.failedPercentage).toFixed(1) : 100}%
              </div>
              <p>Orders that successfully reach the broker</p>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">📈</div>
              <h3>Order Volume</h3>
            </div>
            <div className="insight-content">
              <div className="insight-metric">
                {metricsData?.summary?.totalCreated?.toLocaleString() || 0}
              </div>
              <p>Total orders created in selected time range</p>
            </div>
          </div>

          <div className="insight-card">
            <div className="insight-header">
              <div className="insight-icon">🔄</div>
              <h3>Completion Rate</h3>
            </div>
            <div className="insight-content">
              <div className="insight-metric">
                {metricsData?.summary?.totalCreated ? 
                  (((metricsData.summary.totalFilled + metricsData.summary.totalCanceled) / 
                    metricsData.summary.totalCreated) * 100).toFixed(1) : 0}%
              </div>
              <p>Orders that have reached a final state</p>
            </div>
          </div>
        </div>
      </div>

      {/* External Dashboards Section */}
      <div className="external-dashboards-section">
        <div className="insights-header">
          <h2>Additional Dashboards</h2>
          <div className="insights-subtitle">
            External analytics and monitoring dashboards
          </div>
        </div>
        
        <div className="insights-grid">
          <div className="insight-card dashboard-link">
            <div className="insight-header">
              <div className="insight-icon">📊</div>
              <h3>Balancing Efficiency Insights</h3>
            </div>
            <div className="insight-content">
              <p>View efficiency and effectiveness metrics for balancing operations</p>
              <div className="insight-actions">
                <button 
                  onClick={() => {
                    window.open("https://grafana-478860069183.us-central1.run.app/goto/9x19M5wHg?orgId=1", '_blank');
                  }}
                  className="insight-action primary"
                >
                  Open Dashboard
                </button>
              </div>
            </div>
          </div>

          <div className="insight-card dashboard-link">
            <div className="insight-header">
              <div className="insight-icon">📈</div>
              <h3>Historical Trading Data</h3>
            </div>
            <div className="insight-content">
              <p>Historical order metrics with unlimited BigQuery data access</p>
              <div className="insight-actions">
                <button 
                  onClick={() => {
                    window.open("https://grafana-478860069183.us-central1.run.app/goto/mQQjG5QNg?orgId=1", '_blank');
                  }}
                  className="insight-action primary"
                >
                  Open Dashboard
                </button>
              </div>
            </div>
          </div>

          <div className="insight-card dashboard-link">
            <div className="insight-header">
              <div className="insight-icon">🔄</div>
              <h3>Pub/Sub Metrics</h3>
            </div>
            <div className="insight-content">
              <p>Queue latency and unacked messages for workflow operations</p>
              <div className="insight-actions">
                <button 
                  onClick={() => {
                    window.open("https://grafana-478860069183.us-central1.run.app/goto/zNK3G5QHR?orgId=1", '_blank');
                  }}
                  className="insight-action primary"
                >
                  Open Dashboard
                </button>
              </div>
            </div>
          </div>

          <div className="insight-card dashboard-link">
            <div className="insight-header">
              <div className="insight-icon">🗄️</div>
              <h3>Database Metrics</h3>
            </div>
            <div className="insight-content">
              <p>CPU usage and connection count monitoring</p>
              <div className="insight-actions">
                <button 
                  onClick={() => {
                    window.open("https://grafana-478860069183.us-central1.run.app/goto/lcSCMcwNR?orgId=1", '_blank');
                  }}
                  className="insight-action primary"
                >
                  Open Dashboard
                </button>
              </div>
            </div>
          </div>

          <div className="insight-card dashboard-link">
            <div className="insight-header">
              <div className="insight-icon">📄</div>
              <h3>Balancing States Metrics</h3>
            </div>
            <div className="insight-content">
              <p>Detailed balancing states analysis and distribution metrics</p>
              <div className="insight-actions">
                <button 
                  onClick={() => {
                    window.location.href = "/balancing-states-metrics";
                  }}
                  className="insight-action primary"
                >
                  Open Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};